import { Badge, Tooltip, Row, Col } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import CircleButton from 'components/modules/CircleButton';
import { DisconnectOutlined, ShareAltOutlined } from '@ant-design/icons';

import { getIndefiniteTerm, getMoment } from 'constants/GlobalConfig';
import BelongsTable from 'components/presentational/pages/UsersManagement/Users/BelongsTable';

const moment = getMoment();

export const createColumns = (
    hasTouchScreen: boolean,
    handleMfaReset: (record: User) => void,
    handleOpenMFAPermissionModal: (record: User) => void,
    isControllableUser: boolean,
    tabSelector: UserManagementTabState,
    globalSelector: GlobalState,
): ColumnsType<User> => {
    return [
        {
            title: 'ユーザーID',
            dataIndex: 'login_id',
            key: 'login_id',
            width: 150,
            render: (login_id, record) => {
                return (
                    <Tooltip
                        mouseEnterDelay={1}
                        title={'UUID:' + record.uuid}
                    >
                        {login_id}
                    </Tooltip>
                );
            },
        },
        {
            title: '多要素認証',
            width: 100,
            dataIndex: 'registered_mfa',
            key: 'registered_mfa',
            render: (_, record) => {
                switch (record.registered_mfa) {
                    case true: {
                        return (
                            <Badge
                                color='green'
                                text='設定済'
                            />
                        );
                    }
                    case false: {
                        return (
                            <Badge
                                status='error'
                                text='未設定'
                            />
                        );
                    }
                }
            },
        },
        {
            dataIndex: 'belongs',
            key: 'organization',
            onCell: () => ({ colSpan: 4 }),
            render: (belongs) => {
                return (
                    <BelongsTable
                        dataSource={filterByTerm(
                            belongs,
                            tabSelector,
                            globalSelector,
                        )}
                    />
                );
            },
            title: '学校',
            width: 150,
        },
        {
            children: [
                {
                    onCell: () => ({ colSpan: 0 }),
                    render: () => null,
                    title: 'クラス名',
                    width: 150,
                },
                {
                    onCell: () => ({ colSpan: 0 }),
                    render: () => null,
                    title: '役割',
                    width: 80,
                },
                {
                    onCell: () => ({ colSpan: 0 }),
                    render: () => null,
                    title: '出席番号',
                    width: 75,
                },
            ],
            title: 'クラス',
        },
        {
            title: '名前',
            children: [
                {
                    title: '姓',
                    width: 100,
                    dataIndex: 'last_name',
                    key: 'last_name',
                },
                {
                    title: '名',
                    width: 100,
                    dataIndex: 'first_name',
                    key: 'first_name',
                },
            ],
        },
        {
            title: '多要素認証登録日時',
            dataIndex: 'registered_at_mfa',
            key: 'registered_at_mfa',
            width: 150,
            render: (_, record) => (
                <>
                    <span>
                        {record.registered_at_mfa
                            ? moment
                                  .unix(record.registered_at_mfa)
                                  .format('YYYY/MM/DD HH:mm')
                            : null}
                    </span>
                </>
            ),
        },
        {
            title: '',
            dataIndex: 'action',
            key: 'action',
            width: 60,
            fixed: 'right',
            render: (_, record) => (
                <Row
                    justify='space-around'
                    align='middle'
                >
                    <Col>
                        <CircleButton
                            danger
                            tooltipProps={{
                                title: hasTouchScreen
                                    ? undefined
                                    : '多要素認証設定を解除',
                            }}
                            className='mr-2'
                            disabled={!record.registered_mfa}
                            size='small'
                            icon={<DisconnectOutlined />}
                            popconfirmProps={{
                                cancelText: 'キャンセル',
                                okText: '解除',
                                onConfirm: () => handleMfaReset(record),
                                placement: 'topRight',
                                title: (
                                    <span>多要素認証設定を解除しますか？</span>
                                ),
                            }}
                        />
                    </Col>

                    {isControllableUser && record.is_general_administrator && (
                        <Col>
                            <CircleButton
                                className='mr-2'
                                size='small'
                                icon={<ShareAltOutlined />}
                                onClick={() =>
                                    handleOpenMFAPermissionModal(record)
                                }
                            />
                        </Col>
                    )}
                </Row>
            ),
        },
    ];
};

/***
 * 検索対象の年度もしくは、無期限の年度のみ rowを返す
 * @param row  フィルター前
 * @return row フィルター済み
 */
const filterByTerm = (
    row: User['belongs'],
    tabSelector: UserManagementTabState,
    globalSelector: GlobalState,
) => {
    const term = tabSelector.search.term_uuid;
    if (!term) {
        return row;
    }
    const infiniteTerm = getIndefiniteTerm(globalSelector.termList).uuid;
    return row.filter(
        (val) =>
            val.school_class.term.uuid === term ||
            val.school_class.term.uuid === infiniteTerm,
    );
};
